import { ThemeEvents } from '@theme/events';

class ProductMediaGallery extends HTMLElement {
  #mainTrack = null;
  #mainSlides = [];
  #thumbnailTrack = null;
  #thumbnailButtons = [];
  #scrollFrame = null;
  #closestSection = null;
  #slidesRevealed = false;

  connectedCallback() {
    this.#closestSection = this.closest('.shopify-section, dialog');
    this.#closestSection?.addEventListener(ThemeEvents.variantUpdated, this.updateMediaGallery);
    this.#initializeNativeGallery();
  }

  disconnectedCallback() {
    this.#closestSection?.removeEventListener(ThemeEvents.variantUpdated, this.updateMediaGallery);
    this.#teardownNativeGallery();
  }

  #initializeNativeGallery() {
    const gallery = this.querySelector('[data-native-product-gallery]');
    if (!gallery) return;

    this.#mainTrack = gallery.querySelector('[data-ref="main-slider"] > .splide__track');
    this.#mainSlides = Array.from(this.#mainTrack?.querySelectorAll(':scope > .splide__list > .splide__slide') || []);
    this.#thumbnailTrack = gallery.querySelector('[data-ref="thumbnail-slider"] > .splide__track');
    this.#thumbnailButtons = Array.from(gallery.querySelectorAll('[data-native-thumbnail-index]'));
    this.#slidesRevealed = false;

    this.#mainTrack?.addEventListener('scroll', this.#onMainScroll, { passive: true });
    this.#mainTrack?.addEventListener('pointerdown', this.#onMainInteraction, { passive: true });
    this.#thumbnailButtons.forEach((button) => button.addEventListener('click', this.#onThumbnailClick));
    gallery.querySelector('[data-native-thumbnail-previous]')?.addEventListener('click', this.#onThumbnailPrevious);
    gallery.querySelector('[data-native-thumbnail-next]')?.addEventListener('click', this.#onThumbnailNext);
    this.#updateActiveThumbnail(0);
  }

  #teardownNativeGallery() {
    const gallery = this.querySelector('[data-native-product-gallery]');
    this.#mainTrack?.removeEventListener('scroll', this.#onMainScroll);
    this.#mainTrack?.removeEventListener('pointerdown', this.#onMainInteraction);
    this.#thumbnailButtons.forEach((button) => button.removeEventListener('click', this.#onThumbnailClick));
    gallery?.querySelector('[data-native-thumbnail-previous]')?.removeEventListener('click', this.#onThumbnailPrevious);
    gallery?.querySelector('[data-native-thumbnail-next]')?.removeEventListener('click', this.#onThumbnailNext);
    if (this.#scrollFrame) cancelAnimationFrame(this.#scrollFrame);
    this.#scrollFrame = null;
  }

  #onThumbnailClick = (event) => {
    const index = Number(event.currentTarget.dataset.nativeThumbnailIndex);
    this.#goTo(index);
  };

  #onThumbnailPrevious = () => this.#goTo(this.#currentIndex() - 1);

  #onThumbnailNext = () => this.#goTo(this.#currentIndex() + 1);

  #onMainInteraction = () => this.#revealSlides();

  #onMainScroll = () => {
    if (this.#scrollFrame) return;
    this.#scrollFrame = requestAnimationFrame(() => {
      this.#scrollFrame = null;
      this.#updateActiveThumbnail(this.#currentIndex());
    });
  };

  #currentIndex() {
    if (!this.#mainTrack || !this.#mainSlides.length) return 0;
    const trackLeft = this.#mainTrack.scrollLeft;
    let nearestIndex = 0;
    let nearestDistance = Number.POSITIVE_INFINITY;
    this.#mainSlides.forEach((slide, index) => {
      const distance = Math.abs(slide.offsetLeft - trackLeft);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestIndex = index;
      }
    });
    return nearestIndex;
  }

  #goTo(index) {
    if (!this.#mainTrack || !this.#mainSlides.length) return;
    const boundedIndex = Math.max(0, Math.min(index, this.#mainSlides.length - 1));
    this.#revealSlides();
    const previousSnapType = this.#mainTrack.style.scrollSnapType;
    const previousScrollBehavior = this.#mainTrack.style.scrollBehavior;

    // Certains navigateurs arrêtent un saut programmatique au point de scroll-snap
    // suivant. On désactive le snap pendant une frame pour atteindre directement
    // la miniature demandée, même lors d'un saut de la première à la dernière image.
    this.#mainTrack.style.scrollSnapType = 'none';
    this.#mainTrack.style.scrollBehavior = 'auto';
    this.#mainTrack.scrollLeft = this.#mainSlides[boundedIndex].offsetLeft;
    this.#updateActiveThumbnail(boundedIndex);

    requestAnimationFrame(() => {
      if (!this.#mainTrack) return;
      this.#mainTrack.style.scrollSnapType = previousSnapType;
      this.#mainTrack.style.scrollBehavior = previousScrollBehavior;
    });
  }

  #revealSlides() {
    if (this.#slidesRevealed) return;
    this.#slidesRevealed = true;

    this.#mainSlides.forEach((slide) => {
      slide.style.contentVisibility = 'visible';
      slide.style.containIntrinsicSize = 'none';
    });
  }

  #updateActiveThumbnail(index) {
    this.#thumbnailButtons.forEach((button, buttonIndex) => {
      if (buttonIndex === index) button.setAttribute('aria-current', 'true');
      else button.removeAttribute('aria-current');
    });

    const activeButton = this.#thumbnailButtons[index];
    if (!activeButton || !this.#thumbnailTrack) return;
    const left = activeButton.offsetLeft - (this.#thumbnailTrack.clientWidth - activeButton.offsetWidth) / 2;
    this.#thumbnailTrack.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
  }

  updateMediaGallery = (event) => {
    const newMediaGallery = event.detail.data.html.querySelector('product-media-gallery [data-native-product-gallery]');
    const currentMediaGallery = this.querySelector('[data-native-product-gallery]');
    if (!newMediaGallery || !currentMediaGallery) return;

    if (currentMediaGallery.innerHTML !== newMediaGallery.innerHTML) {
      this.#teardownNativeGallery();
      currentMediaGallery.replaceWith(newMediaGallery);
      this.#initializeNativeGallery();
    }
  };
}

if (!customElements.get('product-media-gallery')) {
  customElements.define('product-media-gallery', ProductMediaGallery);
}
